import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useSettings } from './SettingsContext';
import { fetchThoughtlyAgents, isThoughtlyConfigured } from '../lib/thoughtly';

const AgentContext = createContext();

export const useAgents = () => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgents must be used within an AgentProvider');
  }
  return context;
};

export const AgentProvider = ({ children }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const { settings } = useSettings();

  // Load agents from localStorage on mount
  useEffect(() => {
    const loadAgents = async () => {
      setLoading(true);

      try {
        const hasThoughtlyConfig =
          Boolean(settings.thoughtlyApiKey && settings.thoughtlyTeamId) || isThoughtlyConfigured();

        if (hasThoughtlyConfig) {
          try {
            const remoteAgents = await fetchThoughtlyAgents({
              apiKey: settings.thoughtlyApiKey,
              teamId: settings.thoughtlyTeamId
            });

            if (Array.isArray(remoteAgents) && remoteAgents.length > 0) {
              const normalisedAgents = remoteAgents.map(agent => ({
                id: agent.id || agent.uuid || agent.agent_id || agent.name,
                name:
                  agent.title ||
                  agent.name ||
                  agent.display_name ||
                  agent.agent_name ||
                  'Thoughtly Agent',
                description: agent.description || agent.summary || 'Thoughtly AI calling agent',
                scenario: agent.scenario || agent.category || (agent.tags?.[0] ?? 'custom'),
                voice: agent.voice || agent.voice_profile || 'natural',
                targetAudience: agent.targetAudience || agent.tags || [],
                thoughtlyAgentId: agent.id || agent.uuid || agent.agent_id || agent.name,
                status: (() => {
                  const statusValue =
                    typeof agent.status === 'string' ? agent.status.trim().toLowerCase() : undefined;

                  if (statusValue) {
                    if (['archived', 'inactive', 'deactivated', 'disabled'].includes(statusValue)) {
                      return 'inactive';
                    }

                    if (['active', 'enabled', 'available', 'live'].includes(statusValue)) {
                      return 'active';
                    }
                  }

                  if (agent.active === false) {
                    return 'inactive';
                  }

                  return 'active';
                })(),
                createdAt: agent.created_at || agent.createdAt || new Date().toISOString(),
                lastUsed: agent.last_used_at || agent.lastUsed || null,
                stats: {
                  totalCalls: agent.metrics?.total_calls || agent.total_calls || 0,
                  successfulCalls: agent.metrics?.successful_calls || agent.successful_calls || 0,
                  successRate: agent.metrics?.success_rate
                    ? Math.round(agent.metrics.success_rate * 100) / 100
                    : agent.success_rate || 0,
                  avgDuration: agent.metrics?.average_duration || agent.avg_duration || '0:00'
                }
              }));

              setAgents(normalisedAgents);
              localStorage.setItem('prosper-crm-agents', JSON.stringify(normalisedAgents));
              return;
            }
          } catch (error) {
            console.error('Failed to load agents from Thoughtly', error);
            toast.error('Unable to load Thoughtly agents. Falling back to local data.');
          }
        }

        const savedAgents = localStorage.getItem('prosper-crm-agents');
        if (savedAgents) {
          setAgents(JSON.parse(savedAgents));
        } else {
          const sampleAgents = [
            {
              id: '1',
              name: 'Sarah - Initial Outreach',
              description: 'Specialized in first contact with life insurance professionals',
              scenario: 'initial_outreach',
              voice: 'professional',
              targetAudience: ['Life Insurance Agents', 'Experienced Agents'],
              thoughtlyAgentId: 'agent_sarah_001',
              status: 'active',
              createdAt: new Date().toISOString(),
              lastUsed: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
              stats: {
                totalCalls: 45,
                successfulCalls: 32,
                successRate: 71,
                avgDuration: '4:32'
              }
            },
            {
              id: '2',
              name: 'Michael - Follow-up Pro',
              description: 'Expert at re-engaging interested prospects',
              scenario: 'follow_up',
              voice: 'friendly',
              targetAudience: ['Life Insurance Agents', 'Career Changers'],
              thoughtlyAgentId: 'agent_michael_002',
              status: 'active',
              createdAt: new Date().toISOString(),
              lastUsed: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
              stats: {
                totalCalls: 28,
                successfulCalls: 24,
                successRate: 86,
                avgDuration: '3:45'
              }
            },
            {
              id: '3',
              name: 'Lisa - Meeting Coordinator',
              description: 'Handles meeting confirmations and scheduling',
              scenario: 'meeting_reminder',
              voice: 'natural',
              targetAudience: ['Life Insurance Agents', 'Top Performers'],
              thoughtlyAgentId: 'agent_lisa_003',
              status: 'active',
              createdAt: new Date().toISOString(),
              lastUsed: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
              stats: {
                totalCalls: 67,
                successfulCalls: 61,
                successRate: 91,
                avgDuration: '2:18'
              }
            },
            {
              id: '4',
              name: 'David - Re-activation Specialist',
              description: 'Revives cold leads and dormant prospects',
              scenario: 'reactivation',
              voice: 'authoritative',
              targetAudience: ['Experienced Agents', 'Independent Agents'],
              thoughtlyAgentId: 'agent_david_004',
              status: 'inactive',
              createdAt: new Date().toISOString(),
              lastUsed: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
              stats: {
                totalCalls: 12,
                successfulCalls: 7,
                successRate: 58,
                avgDuration: '5:12'
              }
            }
          ];

          setAgents(sampleAgents);
          localStorage.setItem('prosper-crm-agents', JSON.stringify(sampleAgents));
        }
      } finally {
        setLoading(false);
      }
    };

    loadAgents();
  }, [settings.thoughtlyApiKey, settings.thoughtlyTeamId]);

  // Save agents to localStorage whenever agents change
  useEffect(() => {
    localStorage.setItem('prosper-crm-agents', JSON.stringify(agents));
  }, [agents]);

  const createAgent = (agentData) => {
    const newAgent = {
      id: Date.now().toString(),
      ...agentData,
      createdAt: new Date().toISOString(),
      lastUsed: null,
      stats: {
        totalCalls: 0,
        successfulCalls: 0,
        successRate: 0,
        avgDuration: '0:00'
      }
    };

    setAgents(prev => [newAgent, ...prev]);
    return newAgent;
  };

  const updateAgent = (id, updates) => {
    setAgents(prev => prev.map(agent => 
      agent.id === id 
        ? { ...agent, ...updates, updatedAt: new Date().toISOString() }
        : agent
    ));
  };

  const deleteAgent = (id) => {
    setAgents(prev => prev.filter(agent => agent.id !== id));
    toast.success('Agent deleted successfully');
  };

  const toggleAgentStatus = (id) => {
    setAgents(prev => prev.map(agent => 
      agent.id === id 
        ? { 
            ...agent, 
            status: agent.status === 'active' ? 'inactive' : 'active',
            updatedAt: new Date().toISOString()
          }
        : agent
    ));
    
    const agent = agents.find(a => a.id === id);
    toast.success(`Agent ${agent?.status === 'active' ? 'deactivated' : 'activated'}`);
  };

  const testAgent = async (id) => {
    setLoading(true);
    try {
      // Simulate API call to Thoughtly
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      updateAgent(id, {
        status: 'testing',
        lastUsed: new Date().toISOString()
      });
      
      toast.success('Test call initiated successfully');
      
      // Reset status after test
      setTimeout(() => {
        updateAgent(id, { status: 'active' });
      }, 5000);
      
    } catch (error) {
      toast.error('Failed to initiate test call');
    } finally {
      setLoading(false);
    }
  };

  const getAgentStats = () => {
    const stats = agents.reduce((acc, agent) => {
      acc.total += 1;
      acc.totalCalls += agent.stats.totalCalls;
      acc.successfulCalls += agent.stats.successfulCalls;
      
      if (agent.status === 'active') acc.active += 1;
      if (agent.status === 'inactive') acc.inactive += 1;
      
      return acc;
    }, {
      total: 0,
      active: 0,
      inactive: 0,
      totalCalls: 0,
      successfulCalls: 0
    });

    stats.avgSuccessRate = stats.totalCalls > 0 
      ? Math.round((stats.successfulCalls / stats.totalCalls) * 100) 
      : 0;
    
    stats.avgDuration = '3:42'; // Mock average duration

    return stats;
  };

  const getAgentById = (id) => {
    return agents.find(agent => agent.id === id);
  };

  const value = {
    agents,
    loading,
    createAgent,
    updateAgent,
    deleteAgent,
    toggleAgentStatus,
    testAgent,
    getAgentStats,
    getAgentById
  };

  return (
    <AgentContext.Provider value={value}>
      {children}
    </AgentContext.Provider>
  );
};